/**
 * Global `window.addEventListener('unhandledrejection', ...)` net for
 * `FetchError`-shaped rejections that never reach React Query and were never
 * caught by a local try/catch.
 *
 * Factory-for-testability, same pattern as `mutation-cache-handler.ts`:
 * `makeUnhandledRejectionHandler()` returns the real listener so tests can
 * import and exercise it directly rather than a hand-rolled copy that could
 * silently diverge from what `main.tsx` actually wires up.
 *
 * No double-toast risk by construction: a rejection already caught by React
 * Query (a `useMutation`/`useQuery` fn throwing) or by a local try/catch
 * never becomes an `unhandledrejection` event in the first place — the
 * browser only fires this event for promises that reach garbage collection
 * (or a macrotask boundary) with no `.catch`/`await`-with-try attached. This
 * listener only ever sees truly-unhandled rejections, so it cannot double
 * up with the MutationCache/QueryCache `onError` toasts.
 *
 * Deliberately duck-types via `isFetchError` (checks `.code` is a string)
 * rather than `err instanceof MiniAppFetchError`: importing the mini-app
 * error class (`mini-apps/_shared/fetch-error.ts`) into this core-bootstrap
 * module would pull mini-app runtime code into the main app bundle, and
 * `instanceof` is fragile across chunk/module duplication anyway.
 * `isFetchError` is the established guard here — `classifyError` in
 * `errors.ts` already relies on it for the same reason.
 *
 * The event param is typed structurally (`{ reason: unknown;
 * preventDefault(): void }`) rather than as `PromiseRejectionEvent`: jsdom
 * does not reliably provide a `PromiseRejectionEvent` constructor, so tests
 * need to pass a plain object shape instead of constructing a real event.
 */
import { WriteRefusedError } from './apiProblemError';
import { isFetchError, notifyError } from './errors';

export function makeUnhandledRejectionHandler(): (event: { reason: unknown; preventDefault(): void }) => void {
  return (event) => {
    if (!isFetchError(event.reason)) return;
    // `apiFetch` already announced it; still mark the rejection handled.
    if (event.reason instanceof WriteRefusedError) {
      event.preventDefault();
      return;
    }
    try {
      notifyError(event.reason, 'unhandled-rejection');
    } catch {}
    event.preventDefault();
  };
}
